import { useEffect, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import type { DocsHubSnapshot } from './snapshot';

/**
 * The number beside Docs in the navigation: the documentation sites the docs
 * hub counts (DX-546), from the same snapshot as the hub's own cards — so the
 * entry and the page it leads to show one number. It counted the documented
 * entities until the hub replaced the list of them (DX-306), then the hub's
 * pages; but every other count in the navigation is a count of entries the
 * reader can open from the page the entry leads to, and 1,312 pages beside a
 * page that lists 58 sites read as a different thing. The sites are the
 * entries; the pages are the hub's own figure.
 *
 * `undefined` until the snapshot has been collected, and the entry then shows
 * no number at all, as the Catalog count does: a count not yet made is not a
 * count of zero.
 *
 * Kept across the column's remounts for the reason useNavCounts keeps its own:
 * the sidebar unmounts on every navigation, and a number that blanks and comes
 * back on each click reads as instability. Unlike the catalog's counts it is
 * the same for every reader — the backend collects it with its own
 * credentials — so it need not be tied to who asked.
 */
let lastKnown: number | undefined;

export const useDocsSiteCount = (): number | undefined => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [count, setCount] = useState<number | undefined>(lastKnown);

  useEffect(() => {
    let active = true;
    (async () => {
      const baseUrl = await discoveryApi.getBaseUrl('github-insights');
      const res = await fetchApi.fetch(`${baseUrl}/docs-hub`);
      if (!res.ok) {
        return;
      }
      const snapshot = (await res.json()) as DocsHubSnapshot;
      lastKnown =
        snapshot.generatedAt === null ? undefined : snapshot.totals.sites;
      if (active) {
        setCount(lastKnown);
      }
    })().catch(() => undefined);
    return () => {
      active = false;
    };
  }, [discoveryApi, fetchApi]);

  return count;
};
